import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import Field from "../Field";
import { Upload, FileText, CheckCircle2, ChevronRight, ChevronLeft, Loader2 } from "lucide-react";
import api from "../../../../../const/axios";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const GAME_TYPES = ["NHN", "Beyin həlqəsi", "Xamsa", "Digər"];

const STEPS = ["Əlaqə", "Paket", "Fayl"];

const SubmitPackageDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [form, setForm] = useState({
    name: "", surname: "", email: "", phone: "",
    tournamentName: "", gameType: "NHN", roundCount: "", questionsPerRound: "", notes: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upd = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const canStep0 = form.name.trim() && form.surname.trim() && form.email.trim();
  const canStep1 = form.tournamentName.trim() && form.roundCount.trim() && form.questionsPerRound.trim();
  const canStep2 = Boolean(file);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const submit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      await api.post("contacts/submit-package", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Xəta baş verdi.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0); setDone(false); setError(null); setFile(null);
    setForm({ name: "", surname: "", email: "", phone: "", tournamentName: "", gameType: "NHN", roundCount: "", questionsPerRound: "", notes: "" });
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Paket göndər</DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
            <p className="font-semibold text-gray-800 dark:text-gray-100">Paket uğurla göndərildi!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Step indicator */}
            <div className="flex items-center gap-1 text-xs">
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <span className={cn("font-semibold px-2 py-0.5 rounded-full",
                    i === step ? "bg-blue-500 text-white" : i < step ? "bg-green-100 text-green-700" : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
                  )}>{i + 1}. {s}</span>
                  {i < STEPS.length - 1 && <span className="text-gray-300">›</span>}
                </React.Fragment>
              ))}
            </div>

            {/* Step 0 – Contact info */}
            {step === 0 && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Ad *"><Input value={form.name} onChange={upd("name")} placeholder="Ad" /></Field>
                  <Field label="Soyad *"><Input value={form.surname} onChange={upd("surname")} placeholder="Soyad" /></Field>
                </div>
                <Field label="E-poçt *"><Input type="email" value={form.email} onChange={upd("email")} placeholder="email@example.com" /></Field>
                <Field label="Telefon"><Input value={form.phone} onChange={upd("phone")} placeholder="+994 50 000 00 00" /></Field>
              </>
            )}

            {/* Step 1 – Package details */}
            {step === 1 && (
              <>
                <Field label="Turnirun adı *"><Input value={form.tournamentName} onChange={upd("tournamentName")} placeholder="Məs: Bakı Çempionatı 2026" /></Field>
                <Field label="Oyun növü *">
                  <select value={form.gameType} onChange={upd("gameType")}
                    className="w-full h-10 rounded-xl border border-gray-200 dark:border-gray-700 px-3 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ring bg-white dark:bg-gray-900">
                    {GAME_TYPES.map(g => <option key={g}>{g}</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Tur sayı *"><Input type="number" min={1} value={form.roundCount} onChange={upd("roundCount")} placeholder="3" /></Field>
                  <Field label="Turda sual sayı *"><Input type="number" min={1} value={form.questionsPerRound} onChange={upd("questionsPerRound")} placeholder="15" /></Field>
                </div>
                <Field label="Qeydlər">
                  <textarea value={form.notes} onChange={upd("notes")} rows={3} placeholder="Əlavə məlumat..."
                    className="w-full rounded-xl border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
                </Field>
              </>
            )}

            {/* Step 2 – File upload */}
            {step === 2 && (
              <>
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed rounded-2xl px-6 py-10 flex flex-col items-center gap-3 cursor-pointer transition-colors",
                    dragOver ? "border-blue-400 bg-blue-50 dark:bg-blue-950" : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                  )}
                >
                  <Upload className="w-8 h-8 text-gray-300" />
                  {file ? (
                    <div className="text-center">
                      <p className="text-sm font-semibold text-blue-600 flex items-center gap-1.5 justify-center dark:text-indigo-300">
                        <FileText className="w-4 h-4" />{file.name}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Faylı buraya sürükləyin</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">.docx · .pdf · .xlsx · .doc · .zip — maks. 50 MB</p>
                    </div>
                  )}
                  <input ref={fileRef} type="file" className="hidden"
                    accept=".docx,.pdf,.xlsx,.doc,.zip"
                    onChange={e => setFile(e.target.files?.[0] ?? null)} />
                </div>
                {error && <p className="text-xs text-red-500">{error}</p>}
              </>
            )}

            {/* Navigation */}
            <div className="grid grid-cols-2 gap-2 pt-1">
              {step > 0 ? (
                <Button variant="outline" onClick={() => setStep(s => (s - 1) as any)} disabled={loading}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Geri
                </Button>
              ) : <div />}
              {step < 2 ? (
                <Button onClick={() => setStep(s => (s + 1) as any)}
                  disabled={(step === 0 && !canStep0) || (step === 1 && !canStep1)}>
                  Növbəti <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={submit} disabled={!canStep2 || loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
                  {loading ? "Göndərilir..." : "Göndər"}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubmitPackageDialog;
